import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

type Notice = { id: number; content: string; expiresAt: number };

export const usePurchaseNotices = (limit = 5, duration = 4000) => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const nextId = useRef(0);

  const showPurchaseNotice = useCallback((content: string) => {
    if (!content.trim()) return;
    const notice = { id: ++nextId.current, content: content.trim(), expiresAt: 0 };
    setNotices(current => [...current.filter(item => !item.expiresAt || item.expiresAt > Date.now()), notice]);
  }, []);
  const clearNotices = useCallback(() => setNotices([]), []);

  useEffect(() => {
    if (!notices.length) return;
    const slots = Math.max(1, limit);
    if (notices.slice(0, slots).some(notice => !notice.expiresAt)) {
      setNotices(current => current.map((notice, index) => index < slots && !notice.expiresAt
        ? { ...notice, expiresAt: Date.now() + duration } : notice));
      return;
    }
    const nextExpiry = Math.min(...notices.filter(notice => notice.expiresAt).map(notice => notice.expiresAt));
    const timer = window.setTimeout(() => {
      setNotices(current => current.filter(notice => !notice.expiresAt || notice.expiresAt > Date.now()));
    }, Math.max(1, nextExpiry - Date.now()));
    return () => window.clearTimeout(timer);
  }, [notices, limit, duration]);

  return { notices: notices.slice(0, Math.max(1, limit)), showPurchaseNotice, clearNotices };
};

const PurchaseNotice = styled.div<{ $admin?: boolean }>`
  position: absolute;
  z-index: 60;
  top: ${props => props.$admin ? '16px' : '100%'};
  right: ${props => props.$admin ? '30px' : '16px'};
  max-width: min(280px, calc(100% - 48px));
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  color: #54a8ff;
  font-family: var(--font-Pretendard-SemiBold);
  font-size: 15px;
  line-height: 1.45;
  text-align: right;
  overflow-wrap: anywhere;
  pointer-events: none;

  > div {
    padding: 6px 0;
  }
`;

export default PurchaseNotice;
